/**
 * Utility functions for reading URL parameters when opening a project
 * 
 * Usage examples:
 * - /projects/123?debug=true&tab=map&zoom=10
 * 
 * In your component:
 *   import { getUrlParam, getUrlParamAsBoolean, getUrlParamAsNumber } from './url_params'
 *   
 *   const debug = getUrlParamAsBoolean('debug')  // true
 *   const tab = getUrlParam('tab')              // 'map'
 *   const zoom = getUrlParamAsNumber('zoom')     // 10
 */

/**
 * Get a URL parameter value as a string
 * @example getUrlParam('tab') returns 'map' for URL ?tab=map
 */
export function getUrlParam(key: string): string | null {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(key);
}

/**
 * Get a URL parameter value as a boolean
 * Returns true if the parameter value is 'true' (case-insensitive)
 */
export function getUrlParamAsBoolean(key: string): boolean {
  const value = getUrlParam(key);
  return value !== null && value.toLowerCase() === 'true';
}

/**
 * Get a URL parameter value as a number
 * Returns null if the parameter is not present or cannot be parsed
 */
export function getUrlParamAsNumber(key: string): number | null {
  const value = getUrlParam(key);
  if (value === null) return null;
  
  const num = parseFloat(value);
  return isNaN(num) ? null : num;
}

/**
 * Get a URL parameter value as an array (comma-separated)
 */
export function getUrlParamAsArray(key: string): string[] {
  const value = getUrlParam(key);
  if (value === null) return [];
  
  return value.split(',').map(v => v.trim()).filter(v => v.length > 0);
}

/**
 * Get all URL parameters as an object
 */
export function getAllUrlParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};
  
  urlParams.forEach((value, key) => {
    params[key] = value;
  });
  
  return params;
}

/**
 * Check if debug mode is enabled via URL parameter
 * @deprecated Use getUrlParamAsBoolean('debug') instead
 */
export function isDebugModeEnabled(): boolean {
  return getUrlParamAsBoolean('debug');
}
